'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CheckIcon, CopyIcon } from 'lucide-react';
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  oneDark,
  oneLight,
} from 'react-syntax-highlighter/dist/esm/styles/prism';

type CodeBlockContextType = {
  code: string;
};

const CodeBlockContext = createContext<CodeBlockContextType>({
  code: '',
});

export type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: string;
  showLineNumbers?: boolean;
  children?: ReactNode;
};

export const CodeBlock = ({
  code,
  language,
  showLineNumbers = false,
  className,
  children,
  ...props
}: CodeBlockProps) => (
  <CodeBlockContext.Provider value={{ code }}>
    <div
      className={cn(
        'relative w-full max-w-full overflow-hidden rounded-md border bg-background text-foreground',
        className
      )}
      {...props}
    >
      <div className="relative overflow-x-auto max-w-full">
        <SyntaxHighlighter
          className="!overflow-x-auto dark:hidden max-w-full"
          codeTagProps={{
            className: 'font-mono text-[10px] sm:text-xs whitespace-pre-wrap break-words',
          }}
          customStyle={{
            margin: 1,
            padding: '0.5rem',
            fontSize: '0.625rem',
            background: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            maxWidth: '100%',
            overflowX: 'auto',

          }}
          language={language}
          lineNumberStyle={{
            color: 'hsl(var(--muted-foreground))',
            paddingRight: '0.5rem',
            minWidth: '0.5rem',
            fontSize: '0.6rem',
          }}
          showLineNumbers={showLineNumbers}
          style={oneLight}
          wrapLines={true}
          wrapLongLines={true}
        >
          {code}
        </SyntaxHighlighter>
        <SyntaxHighlighter
          className="hidden !overflow-x-auto dark:block max-w-full"
          codeTagProps={{
            className: 'font-mono text-[10px] sm:text-xs whitespace-pre-wrap break-words',
          }}
          customStyle={{
            margin: 0,
            padding: '0.5rem',
            fontSize: '0.625rem',
            background: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            maxWidth: '100%',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
          language={language}
          lineNumberStyle={{
            color: 'hsl(var(--muted-foreground))',
            paddingRight: '0.5rem',
            minWidth: '1.5rem',
            fontSize: '0.6rem',
          }}
          showLineNumbers={showLineNumbers}
          style={oneDark}
          wrapLines={true}
          wrapLongLines={true}
        >
          {code}
        </SyntaxHighlighter>
        {children && (
          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 flex items-center gap-1 sm:gap-2">
            {children}
          </div>
        )}
      </div>
    </div>
  </CodeBlockContext.Provider>
);

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  ...props
}: CodeBlockCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { code } = useContext(CodeBlockContext);

  const copyToClipboard = async () => {
    if (typeof window === 'undefined' || !navigator.clipboard.writeText) {
      onError?.(new Error('Clipboard API not available'));
      return;
    }

    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      onCopy?.();
      setTimeout(() => setIsCopied(false), timeout);
    } catch (error) {
      onError?.(error as Error);
    }
  };

  const Icon = isCopied ? CheckIcon : CopyIcon;

  return (
    <Button
      className={cn('shrink-0 h-7 w-7 sm:h-8 sm:w-8', className)}
      onClick={copyToClipboard}
      size="icon"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon className="h-3 w-3 sm:h-4 sm:w-4" />}
    </Button>
  );
};
